<template>
  <div class="q-pa-md">
    <!-- Header with Back button -->
    <div class="row items-center q-mb-md">
      <q-btn
        flat
        round
        icon="arrow_back"
        color="primary"
        @click="onBack"
      />
      <span class="text-h6 text-primary q-ml-sm">Customer Detail</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="row justify-center q-pa-xl">
      <q-spinner color="primary" size="50px" />
    </div>

    <!-- Customer Not Found -->
    <div v-else-if="!customer" class="bg-white q-pa-md my-box">
      <div class="text-primary text-center">Customer not found.</div>
    </div>

    <!-- Customer Data -->
    <div v-else>
      <!-- Section 1: Customer Info -->
      <div class="section-card q-mb-md">
        <div class="section-header">
          <span class="section-title">Customer Information</span>
          <q-btn
            flat
            dense
            color="primary"
            label="Edit"
            icon="edit"
            no-caps
            @click="onEditCustomer"
          />
        </div>
        <div class="row q-col-gutter-md">
          <div class="col-12 col-sm-4">
            <div class="info-label">Name</div>
            <div class="info-value">{{ customer.firstname }} {{ customer.lastname }}</div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="info-label">Phone</div>
            <div class="info-value">{{ customer.phone_number || '-' }}</div>
          </div>
          <div class="col-12 col-sm-4">
            <div class="info-label">Email</div>
            <div class="info-value">{{ customer.email || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- Section 2: Total Sales Summary -->
      <div class="row q-col-gutter-md q-mb-md">
        <div class="col-6">
          <div class="stat-card">
            <div class="stat-label">Total Purchase</div>
            <div class="stat-value">{{ customer.totalSaleVolume | formattedPrice }}</div>
          </div>
        </div>
        <div class="col-6">
          <div class="stat-card">
            <div class="stat-label">Total Orders</div>
            <div class="stat-value">{{ customer.totalOrder }}</div>
          </div>
        </div>
      </div>

      <!-- Section 3: Order History & Frequently Bought Products -->
      <div class="row q-col-gutter-md q-mb-md">
        <!-- Order History -->
        <div class="col-12 col-md-6">
          <div class="section-card full-height">
            <div class="row items-center justify-between q-mb-sm">
              <div class="section-title">Order History</div>
              <div class="text-caption text-grey">
                <span v-if="ordersLoading">
                  Loading {{ ordersLoadedCount }} of {{ customer.orderList.length }}...
                </span>
                <span v-else>
                  {{ allOrdersData.length }} orders
                </span>
              </div>
            </div>
            <q-linear-progress
              v-if="ordersLoading"
              :value="ordersLoadedCount / customer.orderList.length"
              color="primary"
              class="q-mb-sm"
            />
            <q-table
              :key="'orders-' + allOrdersData.length"
              dense
              flat
              bordered
              :rows="orderRows"
              :data="orderRows"
              :columns="orderColumns"
              row-key="orderID"
              :pagination="{ rowsPerPage: 10 }"
              :loading="ordersLoading"
            />
          </div>
        </div>

        <!-- Frequently Bought Products -->
        <div class="col-12 col-md-6">
          <div class="section-card full-height">
            <div class="section-title q-mb-sm">Frequently Bought Products</div>
            <q-table
              :key="'products-' + (customer ? customer.productList.length : 0)"
              dense
              flat
              bordered
              :rows="frequentProductList"
              :data="frequentProductList"
              :columns="productColumns"
              row-key="cprcode"
              :pagination="{ rowsPerPage: 10 }"
              :loading="loading"
            />
          </div>
        </div>
      </div>

      <!-- Section 4: Sales by Date Range -->
      <div class="section-card">
        <div class="section-title q-mb-md">Sales Trend</div>

        <!-- Filter Controls -->
        <div class="row q-col-gutter-md q-mb-md items-end">
          <div class="col-12 col-sm-4">
            <q-select
              outlined
              dense
              v-model="chartFilter.mode"
              :options="['By Day', 'By Week', 'By Month']"
              label="Display Mode"
            />
          </div>
          <div class="col-12 col-sm-6">
            <date-picker
              v-if="chartFilter.mode === 'By Day'"
              v-model="chartFilter.dateRange"
              value-type="date"
              placeholder="Select date range"
              range
              class="full-width"
            />
            <date-picker
              v-else-if="chartFilter.mode === 'By Week'"
              v-model="chartFilter.dateRange"
              type="week"
              placeholder="Select week range"
              range
              class="full-width"
            />
            <date-picker
              v-else-if="chartFilter.mode === 'By Month'"
              v-model="chartFilter.dateRange"
              type="month"
              placeholder="Select month range"
              range
              class="full-width"
            />
          </div>
          <div class="col-12 col-sm-2">
            <q-btn
              color="primary"
              label="Apply"
              no-caps
              unelevated
              class="full-width"
              @click="onApplyFilter"
              :loading="chartLoading"
            />
          </div>
        </div>

        <!-- Chart -->
        <div v-if="chartLoading" class="row justify-center q-pa-lg">
          <q-spinner color="primary" size="40px" />
        </div>
        <div v-else-if="chartDataLoaded">
          <div v-if="chartSeries[0].data.length > 0">
            <apexchart
              type="line"
              height="300"
              :options="chartOptions"
              :series="chartSeries"
            />
          </div>
          <div v-else class="text-center q-pa-md text-grey">
            No orders in the selected period.
          </div>
          <!-- Period Summary -->
          <div class="row q-mt-md q-col-gutter-md">
            <div class="col-6 col-sm-3">
              <div class="stat-card-small">
                <div class="stat-label">Period Sales</div>
                <div class="stat-value-small">{{ periodSales | formattedPrice }}</div>
              </div>
            </div>
            <div class="col-6 col-sm-3">
              <div class="stat-card-small">
                <div class="stat-label">Period Orders</div>
                <div class="stat-value-small">{{ periodOrders }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center q-pa-lg text-grey">
          Click "Apply" to load chart data.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

dayjs.extend(customParseFormat);

export default {
  name: "CustomerDetail",
  components: {
    DatePicker,
  },
  data() {
    return {
      loading: true,
      customer: null,
      ordersLoading: false,
      ordersLoadedCount: 0,
      ordersBatchSize: 50, // Load 50 orders in parallel at a time
      chartLoading: false,
      chartDataLoaded: false,
      allOrdersData: [],
      chartFilter: {
        mode: "By Month",
        dateRange: null,
      },
      orderDataList: [],
      productColumns: [
        {
          name: "cprcode",
          align: "left",
          label: "Product Code",
          field: "cprcode",
          sortable: true,
        },
        {
          name: "name",
          align: "left",
          label: "Product Name",
          field: "productName",
          sortable: false,
        },
        {
          name: "quantity",
          align: "center",
          label: "Qty",
          field: "quantity",
          sortable: true,
        },
      ],
      orderColumns: [
        {
          name: "orderID",
          align: "left",
          label: "Order ID",
          field: "orderID",
          sortable: true,
        },
        {
          name: "date",
          align: "left",
          label: "Date",
          field: "dateStr",
          sortable: true,
        },
        {
          name: "amount",
          align: "right",
          label: "Amount (฿)",
          field: "amount",
          format: (val) => new Intl.NumberFormat("th-TH").format(val),
          sortable: true,
        },
      ],
      chartSeries: [{
        name: "Sales",
        data: [],
      }],
      chartOptions: {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        colors: ["#2c2a74"],
        xaxis: {
          type: "datetime",
          labels: {
            datetimeFormatter: {
              year: "yyyy",
              month: "MMM 'yy",
              day: "dd MMM",
            },
          },
        },
        yaxis: {
          title: { text: "Sales Amount" },
          labels: {
            formatter: (value) => {
              return new Intl.NumberFormat("th-TH").format(value);
            },
          },
        },
        tooltip: {
          x: { format: "dd MMM yyyy" },
          y: {
            formatter: (value) => {
              return new Intl.NumberFormat("th-TH").format(value) + " ";
            },
          },
        },
        markers: {
          size: 5,
          hover: { size: 7 },
        },
        grid: {
          borderColor: "#e7e7e7",
        },
      },
    };
  },
  computed: {
    customerId() {
      return this.$route.params.id;
    },
    periodSales() {
      return this.orderDataList.reduce((sum, order) => sum + (order.amount || 0), 0);
    },
    periodOrders() {
      return this.orderDataList.length;
    },
    frequentProductList() {
      if (!this.customer || !this.customer.productList) return [];
      const products = [...this.customer.productList];
      products.sort((a, b) => (a.quantity > b.quantity ? -1 : 1));
      return products.slice(0, 10);
    },
    orderRows() {
      return [...this.allOrdersData].sort((a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    },
  },
  mounted() {
    this.initDateRange();
    this.loadCustomerData();
  },
  methods: {
    initDateRange() {
      const end = dayjs();
      const start = dayjs().subtract(1, "year");
      this.chartFilter.dateRange = [start.toDate(), end.toDate()];
    },
    async loadCustomerData() {
      this.loading = true;
      try {
        let nextToken = null;
        let found = null;

        // Keep loading pages until customer is found or no more pages
        do {
          const result = await this.$services.customerStat.listAllStat(nextToken);
          found = result.items.find(
            (item) => item.customer.id === this.customerId
          );

          if (found) {
            break;
          }

          nextToken = result.nextToken;
        } while (nextToken);

        if (found) {
          this.customer = {
            id: found.customer.id,
            firstname: found.customer.firstname,
            lastname: found.customer.lastname,
            email: found.customer.email,
            phone_number: found.customer.phone_number,
            totalSaleVolume: found.totalSaleVolume,
            totalOrder: found.totalOrder,
            productList: found.productList || [],
            orderList: found.orderList || [],
          };
          // Load orders immediately
          this.loadOrders();
        }
      } catch (error) {
        console.error("Error loading customer data:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadOrders() {
      if (!this.customer || !this.customer.orderList || this.customer.orderList.length === 0) {
        return;
      }

      this.ordersLoading = true;
      this.ordersLoadedCount = 0;

      try {
        const orderList = this.customer.orderList;
        const allLoadedOrders = [];

        // Load orders in parallel batches
        for (let i = 0; i < orderList.length; i += this.ordersBatchSize) {
          const batch = orderList.slice(i, i + this.ordersBatchSize);

          // Load batch in parallel
          const batchResults = await Promise.all(
            batch.map(async (orderId) => {
              try {
                const response = await this.$services.orders.getPOSOrder(orderId);
                const orderData = JSON.parse(response.data);
                const orderDate = dayjs(response.storeDate.toString(), "YYYYMMDD");

                return {
                  orderID: orderId,
                  amount: orderData.posAmount || 0,
                  date: orderDate.toDate(),
                  dateStr: orderDate.format("YYYY-MM-DD"),
                };
              } catch (err) {
                console.error("Error loading order:", orderId, err);
                return null;
              }
            })
          );

          // Filter out failed loads and add to results
          allLoadedOrders.push(...batchResults.filter(Boolean));
          this.ordersLoadedCount = Math.min(i + this.ordersBatchSize, orderList.length);
        }

        // Assign all at once for Vue reactivity
        this.allOrdersData = allLoadedOrders;

        // Set date range and load chart
        if (allLoadedOrders.length > 0) {
          const sortedOrders = [...allLoadedOrders].sort((a, b) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
          );
          const firstOrderDate = dayjs(sortedOrders[0].date);
          const today = dayjs();

          // Offset start by 3 months for "By Month" mode to smooth the chart
          const startDate = this.chartFilter.mode === "By Month"
            ? firstOrderDate.subtract(3, "month")
            : firstOrderDate;

          this.chartFilter.dateRange = [startDate.toDate(), today.toDate()];
          this.onApplyFilter();
        }
      } catch (error) {
        console.error("Error loading orders:", error);
      } finally {
        this.ordersLoading = false;
      }
    },
    async onApplyFilter() {
      if (this.allOrdersData.length === 0) {
        return;
      }

      this.chartLoading = true;

      try {
        // Filter by date range if provided
        if (this.chartFilter.dateRange && this.chartFilter.dateRange.length === 2) {
          const startDate = dayjs(this.chartFilter.dateRange[0]);
          const endDate = dayjs(this.chartFilter.dateRange[1]);

          this.orderDataList = this.allOrdersData.filter((order) => {
            const orderDate = dayjs(order.date);
            return orderDate.isAfter(startDate.subtract(1, "day")) &&
                   orderDate.isBefore(endDate.add(1, "day"));
          });
        } else {
          this.orderDataList = [...this.allOrdersData];
        }

        // Generate chart data
        this.generateChartData();
        this.chartDataLoaded = true;
      } catch (error) {
        console.error("Error applying filter:", error);
      } finally {
        this.chartLoading = false;
      }
    },
    generateChartData() {
      if (this.orderDataList.length === 0) {
        this.chartSeries = [{ name: "Sales", data: [] }];
        return;
      }

      const mode = this.chartFilter.mode;
      const groupedData = {};

      // Group orders by period
      this.orderDataList.forEach((order) => {
        let key;
        const date = dayjs(order.date);

        switch (mode) {
          case "By Week":
            key = date.startOf("week").format("YYYY-MM-DD");
            break;
          case "By Month":
            key = date.startOf("month").format("YYYY-MM-DD");
            break;
          default: // By Day
            key = date.format("YYYY-MM-DD");
            break;
        }

        if (!groupedData[key]) {
          groupedData[key] = 0;
        }
        groupedData[key] += order.amount;
      });

      // Get date range from orders if filter is not set
      const sortedOrders = [...this.orderDataList].sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const minOrderDate = dayjs(sortedOrders[0].date);
      const maxOrderDate = dayjs(sortedOrders[sortedOrders.length - 1].date);

      const startDate = this.chartFilter.dateRange && this.chartFilter.dateRange[0]
        ? dayjs(this.chartFilter.dateRange[0])
        : minOrderDate;
      const endDate = this.chartFilter.dateRange && this.chartFilter.dateRange[1]
        ? dayjs(this.chartFilter.dateRange[1])
        : maxOrderDate;

      const filledData = [];
      let current = startDate;
      let increment;
      let format;

      switch (mode) {
        case "By Week":
          current = startDate.startOf("week");
          increment = "week";
          format = "YYYY-MM-DD";
          break;
        case "By Month":
          current = startDate.startOf("month");
          increment = "month";
          format = "YYYY-MM-DD";
          break;
        default:
          increment = "day";
          format = "YYYY-MM-DD";
          break;
      }

      while (current.isBefore(endDate.add(1, "day"))) {
        const key = current.format(format);
        filledData.push({
          x: current.valueOf(),
          y: groupedData[key] || 0,
        });
        current = current.add(1, increment);
      }

      this.chartSeries = [{
        name: "Sales",
        data: filledData,
      }];
    },
    onBack() {
      this.$router.go(-1);
    },
    onEditCustomer() {
      // Set active customer in store and navigate to update page
      this.$store.commit("activeCustomer/setCustomer", {
        id: this.customer.id,
        firstname: this.customer.firstname,
        lastname: this.customer.lastname,
        email: this.customer.email,
        phone_number: this.customer.phone_number,
        address: [],
      });
      this.$router.push({ name: "UpdateCustomer" });
    },
  },
};
</script>

<style scoped>
.section-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c2a74;
}

.info-label {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #333;
}

.stat-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-card-small {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #2c2a74;
}

.stat-value-small {
  font-size: 18px;
  font-weight: 600;
  color: #2c2a74;
}

.full-height {
  height: 100%;
}

>>> .mx-input {
  height: 40px !important;
  border-color: #e0e0e0 !important;
}

>>> .mx-datepicker {
  width: 100%;
}

>>> .q-table--bordered {
  border: 1px solid #e0e0e0;
}

>>> .q-table thead th {
  font-weight: 600;
  color: #2c2a74;
}
</style>
